import { Alert, Button, message } from "antd";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import { membersService } from "../../service/members.service";

// Copy states the exact problem, keyed on the failure cause recorded by the
// Stripe webhooks. The self-serve action is the same hosted invoice page in
// every case (it collects 3DS verification as well as payment) — only the
// wording and button label change.
const BANNER_COPY = {
  payment_failed: {
    member: "Your team's payment didn't go through. Please contact your admin to fix it.",
    admin: "Your team's payment didn't go through. Pay now to keep your team's access.",
    solo: "Your payment didn't go through. Pay now to keep access.",
    button: "Pay now",
  },
  payment_action_required: {
    member: "Your team's payment needs bank verification. Please contact your admin.",
    admin: "Your bank needs to verify your team's payment. Verify it to keep your team's access.",
    solo: "Your bank needs to verify your payment. Verify it to keep access.",
    button: "Verify payment",
  },
  invoice_overdue: {
    member: "Your team's invoice is overdue. Please contact your admin.",
    admin: "Your team's invoice is overdue. Pay it to keep your team's access.",
    solo: "Your invoice is overdue. Pay it to keep access.",
    button: "Pay invoice",
  },
} as const;

// Shown while a subscription payment is failing but Stripe is still retrying
// (before any cancellation). Solo users and group admins get a pay/verify
// button (Stripe hosted invoice / billing portal); group members are asked
// to contact their administrator.
const PaymentFailingBanner = () => {
  const { userInfo } = useAppSelector((state) => state.auth);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Re-arm after the failure clears so a NEW failure months later still shows,
  // and whenever the cause changes: dismissing the mild "your bank needs to
  // verify this" banner must not also suppress the "your payment didn't go
  // through" one it escalates into. The SPA never remounts and the flag stays
  // true across that transition, so without the reason in here the urgent
  // message would never be shown for the rest of the session.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are the reset triggers, not values the effect reads
  useEffect(() => {
    setDismissed(false);
  }, [userInfo.billing_payment_failing, userInfo.billing_payment_failing_reason]);

  if (!userInfo.billing_payment_failing || dismissed) return null;

  const isAdmin = userInfo.is_group_admin;
  const isGroupMember = Boolean(userInfo.group_name) && !isAdmin;

  const copy = BANNER_COPY[userInfo.billing_payment_failing_reason ?? "payment_failed"] ?? BANNER_COPY.payment_failed;
  const bannerMessage = isGroupMember ? copy.member : isAdmin ? copy.admin : copy.solo;

  const handleFixPayment = async () => {
    setLoading(true);
    // Open the tab synchronously in the click gesture — Safari blocks
    // window.open after an await.
    const payTab = window.open("", "_blank");
    try {
      const { portal_url } = await membersService.openBillingPortal("pay");
      if (payTab) {
        payTab.location.href = portal_url;
      } else {
        window.location.href = portal_url;
      }
    } catch {
      payTab?.close();
      message.error("Couldn't open the billing page. Please try again or contact sales@getnudgeai.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Alert
      banner
      // Red in every dunning state: access is at risk in all of them, so the
      // banner should read as an error rather than an advisory.
      type="error"
      closable
      onClose={() => setDismissed(true)}
      message={bannerMessage}
      action={
        isGroupMember ? undefined : (
          <Button size="small" type="primary" loading={loading} onClick={handleFixPayment}>
            {copy.button}
          </Button>
        )
      }
      style={{ justifyContent: "flex-start", textAlign: "left" }}
    />
  );
};

export default PaymentFailingBanner;
